export type ProductCategory =
  | 'whatsapp-class'
  | 'short-course'
  | 'ebook'
  | 'resource';

export interface Product {
  id: string;
  title: string;
  description: string;
  category: ProductCategory;
  isFree: boolean;
  price: string; // display price, e.g. "₦15,000" or "FREE"
  priceValue: number; // numeric NGN value; 0 for free
  image: string;
  hours?: number;
  students?: number;
  rating?: number;
  author?: string;
  downloads?: string;
  downloadUrl?: string; // for free downloadable resources / ebooks
  whatsappLink?: string;
}

export const categoryLabels: Record<ProductCategory, string> = {
  'whatsapp-class': 'WhatsApp Classes',
  'short-course': 'Short Courses',
  ebook: 'E-books',
  resource: 'Free Resources',
};

const wa = 'https://wa.me/c/2349058681595';

export const products: Product[] = [
  // ===== WhatsApp Classes (self-taught) =====
  {
    id: 'foodstuff-export',
    title: 'Foodstuffs Export',
    description:
      'Master the essentials of exporting Nigerian foodstuffs to international markets. Learn NAFDAC certification, packaging, and buyer acquisition.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦20,000',
    priceValue: 20000,
    image:
      'https://images.unsplash.com/photo-1509358271058-acd22cc93898?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 250,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'mini-exporting',
    title: 'Mini Exportation',
    description: 'Start small-scale export operations with minimal capital investment.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦15,000',
    priceValue: 15000,
    image:
      'https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 3,
    students: 180,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'buyer-sourcing',
    title: 'Buyer Sourcing',
    description: 'Strategies for finding and connecting with international buyers.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦25,000',
    priceValue: 25000,
    image:
      'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 5,
    students: 320,
    rating: 4.9,
    whatsappLink: wa,
  },
  {
    id: 'incoterms-2020',
    title: 'INCOTERMS 2020',
    description: 'A comprehensive guide to understanding and applying International Commercial Terms.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦18,000',
    priceValue: 18000,
    image:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 200,
    rating: 4.6,
    whatsappLink: wa,
  },
  {
    id: 'export-documentation',
    title: 'Export Documentation',
    description: 'Master all essential documentation required for successful international trade.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦25,000',
    priceValue: 25000,
    image:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 6,
    students: 280,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'export-financing',
    title: 'Export Financing',
    description: 'Financing options and strategies for export operations.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦30,000',
    priceValue: 30000,
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 150,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'export-business-planning',
    title: 'Export Business Planning',
    description: 'Develop comprehensive business plans for successful export ventures.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦25,000',
    priceValue: 25000,
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 5,
    students: 220,
    rating: 4.9,
    whatsappLink: wa,
  },
  {
    id: 'foundation-commodities',
    title: 'Foundation in Commodities',
    description: 'Understanding commodity trading basics and international market dynamics.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦18,000',
    priceValue: 18000,
    image:
      'https://images.unsplash.com/photo-1509358271058-acd22cc93898?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 190,
    rating: 4.6,
    whatsappLink: wa,
  },
  {
    id: 'export-contract-analysis',
    title: 'Export Contract Analysis',
    description: 'Analyze and draft export contracts to avoid legal pitfalls and protect your interests.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦20,000',
    priceValue: 20000,
    image:
      'https://www.technologyhq.org/wp-content/uploads/2022/12/Everything-you-need-know-about-contract-analysis-696x464.jpg',
    hours: 4,
    students: 160,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'ai-buyer-sourcing',
    title: 'Buyer Sourcing in the Age of AI',
    description: 'Use AI tools to find reliable international buyers quickly and efficiently.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦25,000',
    priceValue: 25000,
    image:
      'https://imageio.forbes.com/specials-images/imageserve/643016813686d8eafca00875/0x0.jpg?format=jpg&width=1200',
    hours: 5,
    students: 210,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'digital-marketing-exporters',
    title: 'Digital Marketing for Exporters',
    description: 'Use digital marketing to promote your export products and generate leads.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦18,000',
    priceValue: 18000,
    image:
      'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 230,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'foundation-international-trade',
    title: 'Foundation in International Trade',
    description: 'Understand the fundamental principles of international trade and global market dynamics.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦15,000',
    priceValue: 15000,
    image:
      'https://th.bing.com/th/id/OIP.CTr19elwVNKScENSAzUCaQHaHa?cb=iwp2&w=480&h=480&rs=1&pid=ImgDetMain',
    hours: 3,
    students: 195,
    rating: 4.6,
    whatsappLink: wa,
  },
  {
    id: 'chatgpt-exporters',
    title: 'How to Use ChatGPT for Exporters',
    description: 'Use ChatGPT for proposals, market research, and automating export tasks.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦15,000',
    priceValue: 15000,
    image:
      'https://th.bing.com/th/id/R.d19dea407bdf0079aee9bea943b158fa?rik=ZGttSx17n4kSjw&pid=ImgRaw&r=0',
    hours: 3,
    students: 170,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'export-desk-setup',
    title: 'How to Set Up Your Export Desk',
    description: 'Step-by-step guide to establishing an efficient export unit within your organization.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦18,000',
    priceValue: 18000,
    image:
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    hours: 4,
    students: 140,
    rating: 4.6,
    whatsappLink: wa,
  },
  {
    id: 'export-business-setup',
    title: 'How to Set Up Your Export Business',
    description: 'From registration to market research, everything you need to launch your export business.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦20,000',
    priceValue: 20000,
    image:
      'https://th.bing.com/th/id/OIP.CTr19elwVNKScENSAzUCaQHaHa?cb=iwp2&w=480&h=480&rs=1&pid=ImgDetMain',
    hours: 4,
    students: 185,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'export-logistics',
    title: 'Mastering Export Logistics',
    description: 'Manage shipping, customs, and freight forwarding for your export business.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦25,000',
    priceValue: 25000,
    image:
      'https://th.bing.com/th/id/OIP.ucAbcJsOjH5DVwPVc5TNMAHaDf?cb=iwp2&rs=1&pid=ImgDetMain',
    hours: 5,
    students: 210,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'intro-to-airfreight',
    title: 'Introduction to Airfreight',
    description: 'How airfreight works and when to use it in your export operations.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦15,000',
    priceValue: 15000,
    image:
      'https://th.bing.com/th/id/OIP.dkYAwZQSCsmyRmG6N2YeuAHaE7?cb=iwp2&rs=1&pid=ImgDetMain',
    hours: 3,
    students: 130,
    rating: 4.6,
    whatsappLink: wa,
  },
  {
    id: 'export-quality-assurance',
    title: 'Quality Assurance in Export',
    description: 'Ensure your goods meet international quality standards and avoid rejections.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦18,000',
    priceValue: 18000,
    image:
      'https://th.bing.com/th/id/R.a67cccd3fdb0a2dd1524053fafc9dc91?rik=d9NkbeIhJ0AkFg&pid=ImgRaw&r=0',
    hours: 4,
    students: 150,
    rating: 4.7,
    whatsappLink: wa,
  },
  {
    id: 'hair-products-export',
    title: 'Become a Hair Products Exporter',
    description: 'Tap into the booming global demand for African hair care products.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦20,000',
    priceValue: 20000,
    image:
      'https://th.bing.com/th/id/OIP.aQgkZxtw44ed5jKCesLmHQHaE8?cb=iwp2&rs=1&pid=ImgDetMain',
    hours: 4,
    students: 170,
    rating: 4.8,
    whatsappLink: wa,
  },
  {
    id: 'afcfta-leverage',
    title: 'How to Leverage AfCFTA for Your Business',
    description: 'Explore opportunities under the African Continental Free Trade Area.',
    category: 'whatsapp-class',
    isFree: false,
    price: '₦22,000',
    priceValue: 22000,
    image:
      'https://sbs.strathmore.edu/wp-content/uploads/2023/11/AfCFTA-A-Path-to-Prosperity-for-Africa.png',
    hours: 5,
    students: 200,
    rating: 4.9,
    whatsappLink: wa,
  },

  // ===== E-books =====
  {
    id: 'ebook-export-fundamentals',
    title: 'Export Management Fundamentals',
    description: 'A comprehensive introduction to the fundamentals of export management.',
    category: 'ebook',
    isFree: true,
    price: 'FREE',
    priceValue: 0,
    image:
      'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    author: 'LSEM Faculty',
    downloads: '2.5K',
    rating: 4.8,
    downloadUrl: 'https://drive.google.com/file/d/1whueHXDXSH1uthrOvnl3DwoykooDGnji/view?usp=sharing',
  },
  {
    id: 'ebook-international-trade-docs',
    title: 'International Trade Documentation',
    description: 'Master the paperwork and documentation required for international trade.',
    category: 'ebook',
    isFree: false,
    price: '₦15,000',
    priceValue: 15000,
    image:
      'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    author: 'Dr. Samuel Johnson',
    downloads: '1.8K',
    rating: 4.9,
  },
  {
    id: 'ebook-buyer-playbook',
    title: 'The Buyer Sourcing Playbook',
    description: 'A practical handbook for finding, qualifying, and closing international buyers.',
    category: 'ebook',
    isFree: false,
    price: '₦12,000',
    priceValue: 12000,
    image:
      'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    author: 'LSEM Faculty',
    downloads: '1.1K',
    rating: 4.7,
  },

  // ===== Free Resources =====
  {
    id: 'resource-emp-details',
    title: 'EMP Program Details',
    description: 'Full brochure covering the Export Mentorship Program structure and outcomes.',
    category: 'resource',
    isFree: true,
    price: 'FREE',
    priceValue: 0,
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    downloads: '3.2K',
    downloadUrl: 'https://drive.google.com/file/d/1whueHXDXSH1uthrOvnl3DwoykooDGnji/view?usp=sharing',
  },
  {
    id: 'resource-emp-curriculum',
    title: 'EMP Program Curriculum',
    description: 'Detailed curriculum breakdown for the Export Mentorship Program.',
    category: 'resource',
    isFree: true,
    price: 'FREE',
    priceValue: 0,
    image:
      'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
    downloads: '2.6K',
    downloadUrl: 'https://drive.google.com/file/d/1zmF2ZPiD0A5pk9Um0HJfpM5EQ4XR5Owg/view?usp=sharing',
  },
];